import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { CheckCircle } from 'lucide-react';
import { FaFireAlt } from 'react-icons/fa';
import type { AppDispatch, RootState } from '../redux/store';
import { createOrder, fetchPlans, verifyOrder } from '../redux/thunks/planThunks'; // 👈 import your thunk
import Sidebar from './Sidebar';
import NavbarApp from './navbarApp';

const Pricing: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { plans, status } = useSelector((state: RootState) => state.plans);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchPlans());
  }, [dispatch]);

  const handleCardMouseEnter = (index: number) => setHoveredCard(index);
  const handleCardMouseLeave = () => setHoveredCard(null);

  const getCardStyle = (index: number): React.CSSProperties => ({
    transform: hoveredCard === index ? 'translateY(-10px)' : 'translateY(0)',
    boxShadow: hoveredCard === index
      ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease'
  });

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuyNow = async (plan: any) => {
    const res = await loadRazorpayScript();
    if (!res) {
      alert('Razorpay SDK failed to load. Are you online?');
      return;
    }
  
    try {
      const orderRes: any = await dispatch(
        createOrder({
          userId: user?.id || undefined,
          planName: plan.name,
          planType: 'monthly',
          customDays: '',
        })
      );
  
      if (createOrder.fulfilled.match(orderRes)) {
        const { orderId, currency, amount } = orderRes.payload;
  
        const options: any = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: amount * 100,
          currency,
          name: 'Dev Innovations Labs',
          description: `${plan.name} Plan Purchase`,
          order_id: orderId,
          handler: async function (response: any) {
            try {
              const orderFinal: any = await dispatch(
                verifyOrder({
                  userId: user?.id || undefined,
                  razorpayOrderId: orderId,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                  amount,
                  currency,
                })
              );
  
              if (verifyOrder.fulfilled.match(orderFinal)) {
                alert('Payment successful!');
                navigate("/tokens");
              } else {
                alert('Payment verification failed!');
              }
            } catch (err) {
              console.error('Payment Error:', err);
              alert('Payment verification failed!');
            }
          },
          prefill: {
            name: user?.name || 'User',
            email: user?.email || 'test@example.com',
          },
          theme: { color: '#1e40af' },
        };
  
        const paymentObject = new (window as any).Razorpay(options);
        paymentObject.open();
      } else {
        alert('Failed to create order!');
      }
    } catch (err) {
      console.error('Payment Error:', err);
      alert('Payment initiation failed');
    }
  };
  

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <NavbarApp user={user} setSidebarOpen={setSidebarOpen} />

      {/* Mobile Sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)} />
        <div className="relative w-64 bg-gray-900 h-full">
          <Sidebar />
        </div>
      </div>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <div className="hidden md:block w-64 bg-blue-900">
          <Sidebar />
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">Plans and Pricing</h1>
            </div>

            {status === 'loading' ? (
              <div className="text-center text-gray-400">Loading plans...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-800 rounded-xl p-6 border border-gray-700 relative transition-all"
                    style={getCardStyle(idx)}
                    onMouseEnter={() => handleCardMouseEnter(idx)}
                    onMouseLeave={handleCardMouseLeave}
                  >
                    {plan.badge === 'Popular' ? (
                      <span className="absolute top-4 right-4 text-white text-xs px-3 py-1 rounded-full font-medium" style={{backgroundColor: '#D22B2B'}}>
                        <div className="flex items-center">
                          <FaFireAlt className="mr-1" />
                          {plan.badge}
                        </div>
                      </span>
                    ) : plan.badge === 'High' ? (
                      <span className="absolute top-4 right-4 bg-orange-900 text-orange-300 text-xs px-3 py-1 rounded-full font-medium">
                        <div className="flex items-center">
                          <FaFireAlt className="mr-1" />
                          {plan.badge}
                        </div>
                      </span>
                    ) :
                      ''
                    }
                    <h2 className="text-2xl font-semibold mb-4">{plan.name}</h2>
                    <div className="text-4xl font-bold mb-2">&#x20B9; {plan.price}</div>
                    <p className="text-sm text-gray-400 mb-6">One-time purchase</p>

                    <p className="text-gray-300 mb-3 font-medium">Features</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature: string, i: number) =>
                        feature ? (
                          <li key={i} className="flex items-center text-gray-300">
                            <CheckCircle className="text-green-500 mr-2 h-5 w-5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ) : (
                          <li key={i} className="invisible">placeholder</li>
                        )
                      )}
                    </ul>

                    <button
                      onClick={() => handleBuyNow(plan)}
                      className={`w-full py-3 px-4 rounded-md font-medium transition duration-150 bg-white text-black hover:bg-gray-200`}
                    >
                      Buy Now
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Pricing;
